import _ from "lodash";
import React from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import AllNflGamesHelper from "../../classes/allNflGamesHelper";
import StatsHelper from "../../classes/statsHelper";
import useNflTeams from "../../hooks/queryResults/nfl/useNflTeams";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useNflGamesForWeek from "../../hooks/queryResults/useNflGamesForWeek";
import useTeamLineups from "../../hooks/queryResults/useTeamLineups";
import useWeek from "../../hooks/queryResults/useWeek";
import useAdminMode from "../../hooks/useAdminMode";
import useCurrentUser from "../../hooks/useCurrentUser";
import useUrls from "../../hooks/useUrls";
import http from "../../services/httpService";
import Util from "../../util/util";
import PlayerImageByPos from "../player/PlayerImageByPos";
import { getPlayerName } from "../player/PlayerName";
import Loading from "../ui/Loading";
import classNames from "classnames";

const StyledTable = styled.table`
  width: 100%;

  & th,
  & td {
    padding: 0.5rem;
  }

  & tr {
    height: 45px;
  }

  & .btn.btn-lineup {
    margin: 0;
  }

  & .btn-red {
    color: red;
    border-color: red;
  }
`;

export default function TeamLineup({ team }) {
  const currentUser = useCurrentUser();
  const { season } = useParams();
  const { selectedWeek } = useWeek();
  const { teamLineupsData, teamLineupsQuery, teamLineupsUrl } = useTeamLineups(
    team,
    selectedWeek
  );
  const { playersData, playersQuery } = useAllPlayers();
  const { nflTeamsData, nflTeamsQuery } = useNflTeams();
  const { nflGamesData, nflGamesQuery } = useNflGamesForWeek(
    season,
    selectedWeek
  );
  const { apiUrl } = useUrls();
  const queryClient = useQueryClient();
  const adminMode = useAdminMode();

  const startMutation = useMutation(
    ({ lineupId, playerId }) => {
      const url = `${apiUrl}/lineups/${lineupId}/${playerId}/start`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with no data`);
      return http.patch(url, null, config);
    },
    {
      onMutate: async ({ __lineupId, playerId }) => {
        await queryClient.cancelQueries(teamLineupsUrl);
        const previousLineups = queryClient.getQueryData(teamLineupsUrl);
        queryClient.setQueryData(teamLineupsUrl, (old) => {
          const newData = _.cloneDeep(old);
          const spots = newData.teamLineups.weeks[selectedWeek].spots;
          for (const spot of spots) {
            if (spot.player === playerId) {
              const player = playersData.players[playerId];
              if (player && player.pos) spot.startingPosition = player.pos;
              break;
            }
          }
          return newData;
        });
        return { previousLineups };
      },
      onSuccess: async () => {
        console.log("start mutation - success");
      },
      onError: (error, newLineup, context) => {
        console.error(error);
        queryClient.setQueryData(teamLineupsUrl, context.previousLineups);
      },
      onSettled: async () => {
        await queryClient.invalidateQueries(teamLineupsUrl);
      },
    }
  );

  const benchMutation = useMutation(
    ({ lineupId, playerId }) => {
      const url = `${apiUrl}/lineups/${lineupId}/${playerId}/bench`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with no data`);
      return http.patch(url, null, config);
    },
    {
      onMutate: async ({ __lineupId, playerId }) => {
        await queryClient.cancelQueries(teamLineupsUrl);
        const previousLineups = queryClient.getQueryData(teamLineupsUrl);
        queryClient.setQueryData(teamLineupsUrl, (old) => {
          const newData = _.cloneDeep(old);
          const spots = newData.teamLineups.weeks[selectedWeek].spots;
          for (const spot of spots) {
            if (spot.player === playerId) {
              const player = playersData.players[playerId];
              if (player && player.pos) spot.startingPosition = null;
              break;
            }
          }
          return newData;
        });
        return { previousLineups };
      },
      onSuccess: async () => {
        console.log("bench mutation - success");
      },
      onError: (error, newLineup, context) => {
        console.error(error);
        queryClient.setQueryData(teamLineupsUrl, context.previousLineups);
      },
      onSettled: async () => {
        await queryClient.invalidateQueries(teamLineupsUrl);
      },
    }
  );

  const isLoading = useIsLoading(
    team,
    teamLineupsData,
    teamLineupsQuery,
    playersData,
    playersQuery,
    nflTeamsData,
    nflTeamsQuery,
    nflGamesData,
    nflGamesQuery
  );
  if (isLoading) return <Loading />;

  const allNflGamesHelper = new AllNflGamesHelper(nflGamesData);
  const canChangeLineup =
    currentUser.isAdmin() || Util.hasOfficialRole(currentUser, team);

  const positions = ["QB", "RB", "WR", "TE", "K", "DEF"];
  const lineupSort = (a, b) => {
    const aPlayer = a.player ? playersData.players[a.player] : null;
    const bPlayer = b.player ? playersData.players[b.player] : null;

    if (aPlayer === null && bPlayer === null) return 0;
    if (aPlayer !== null && bPlayer === null) return -1;
    if (aPlayer === null && bPlayer !== null) return 1;

    if (aPlayer && bPlayer) {
      if (!aPlayer.pos && !bPlayer.pos) return 0;
      if (aPlayer.pos && !bPlayer.pos) return -1;
      if (!aPlayer.pos && bPlayer.pos) return 1;

      if (a.startingPosition && !b.startingPosition) return -1;
      if (!a.startingPosition && b.startingPosition) return 1;

      const aPosIndex = positions.indexOf(aPlayer.pos);
      const bPosIndex = positions.indexOf(bPlayer.pos);
      if (aPosIndex === bPosIndex) {
        if (a.num === b.num) return 0;
        return a.num < b.num ? -1 : 1;
      }
      return aPosIndex < bPosIndex ? -1 : 1;
    }
  };

  const lineupId = teamLineupsData.teamLineups.weeks[selectedWeek].id;
  const spots = teamLineupsData.teamLineups.weeks[selectedWeek].spots;
  spots.sort(lineupSort);

  const starters = spots.filter((spot) => !!spot.startingPosition);

  const startingSpots = [
    { isEmptySpot: true, startingPosition: "QB" },
    { isEmptySpot: true, startingPosition: "RB" },
    { isEmptySpot: true, startingPosition: "RB" },
    { isEmptySpot: true, startingPosition: "WR" },
    { isEmptySpot: true, startingPosition: "WR" },
    { isEmptySpot: true, startingPosition: "WR" },
    { isEmptySpot: true, startingPosition: "TE" },
    { isEmptySpot: true, startingPosition: "K" },
    { isEmptySpot: true, startingPosition: "DEF" },
  ];
  starters.forEach((spot) => {
    let i = 0;
    for (const startingSpot of startingSpots) {
      if (
        startingSpot.isEmptySpot &&
        startingSpot.startingPosition === spot.startingPosition
      ) {
        startingSpots[i] = spot;
        break;
      }
      i++;
    }
  });

  const openPositions = [];
  startingSpots.forEach((spot) => {
    if (spot.isEmptySpot) {
      if (!openPositions.includes(spot.startingPosition)) {
        openPositions.push(spot.startingPosition);
      }
    }
  });

  const bench = spots.filter((spot) => !spot.startingPosition);

  const headerRow = (
    <tr>
      {canChangeLineup ? <th>Action</th> : null}
      <th>Pos</th>
      <th>&nbsp;</th>
      <th>Player</th>
      <th title={"Opponent"}>Opp</th>
      <th>Score</th>
    </tr>
  );

  return (
    <StyledTable>
      <thead>{headerRow}</thead>
      <tbody>
        {[startingSpots, bench].map((players, i) => {
          const playerRows = players.map((spot, i) => {
            let actionCell = null;
            let pos = null;
            let playerImage = null;
            let playerName = null;
            // let playerNflTeamAbbr = null;
            let matchup = null;
            let matchupCell = null;
            let matchupTime = null;
            let player = null;
            let playerScore = null;
            let gameHasStarted = false;
            let gameHasEnded = false;
            let playerScoreCell;

            const isStarter = spot.startingPosition;
            const isBench = !isStarter;

            const spotHasPlayer = !!spot.player;

            if (spotHasPlayer) {
              player = playersData.players[spot.player];
              pos = player.pos;
              playerName = getPlayerName(player, { injury: 1, nfl: 1 });

              const hasNflTeam = !!player.nflTeamAbbr;

              if (hasNflTeam) {
                const nflGame = allNflGamesHelper.getTeamGame(
                  selectedWeek,
                  player.nflTeamAbbr
                );

                const hasNflGame = !!nflGame;

                gameHasStarted = allNflGamesHelper.hasNflGameStarted(nflGame);
                gameHasEnded = !!nflGame?.hasEnded;

                const isAnimated = gameHasStarted && !gameHasEnded;

                playerImage = (
                  <div
                    style={{ backgroundColor: "#00ab00", padding: "0.25rem" }}
                  >
                    <PlayerImageByPos
                      isAnimated={isAnimated}
                      player={player}
                      style={{
                        maxHeight: "18px",
                      }}
                    />
                  </div>
                );

                // playerNflTeamAbbr = NflUtil.formatNflTeamAbbr(
                //   player.nflTeamAbbr
                // );

                matchup = allNflGamesHelper.getMatchupText(
                  selectedWeek,
                  player.nflTeamAbbr
                );
                // default to bye
                matchupCell = <span style={{ color: "red" }}>bye</span>;

                if (hasNflGame) {
                  // overwrite with matchup
                  matchupCell = matchup;

                  matchupTime = allNflGamesHelper.getMatchUpTextNew(
                    nflGame,
                    player.nflTeamAbbr
                  );

                  if (gameHasStarted) {
                    if (spot.hasOwnProperty("score")) {
                      playerScore = spot.score.toFixed(2);

                      if (spot.hasOwnProperty("stats")) {
                        const summary = StatsHelper.getSummary(pos, spot.stats);
                        playerScoreCell = (
                          <span title={summary.join("\n")}>{playerScore}</span>
                        );
                      } else {
                        playerScoreCell = playerScore;
                      }
                    } else {
                      playerScoreCell = (
                        <span title={"No statistics recorded"}>0.00</span>
                      );
                    }
                  } else {
                    if (spot.hasOwnProperty("atg")) {
                      const atg = spot?.atg.toFixed(2);
                      const avg = spot?.avg.toFixed(2);

                      const title = [`${atg} pts per team game`];
                      if (atg !== avg) title.push(`${avg} pts per game played`);

                      playerScoreCell = (
                        <span
                          title={`Averaging ${title.join(", ")}`}
                          style={{ opacity: 0.5 }}
                        >
                          {atg}
                        </span>
                      );
                    }
                  }
                }
              }

              if (canChangeLineup && player) {
                const disabled = isBench && !openPositions.includes(player.pos);

                const isLocked =
                  spot.locked || gameHasStarted || (!matchup && isBench);

                const lockedClass = isLocked ? "btn-red" : "";

                const startButton = (
                  <button
                    className={classNames("btn", "btn-lineup", lockedClass)}
                    disabled={disabled}
                    onClick={() =>
                      startMutation.mutate({
                        lineupId,
                        playerId: player.id,
                      })
                    }
                  >
                    Start
                  </button>
                );

                const benchButton = (
                  <button
                    className={classNames("btn", "btn-lineup", lockedClass)}
                    onClick={() =>
                      benchMutation.mutate({
                        lineupId,
                        playerId: player.id,
                      })
                    }
                  >
                    Bench
                  </button>
                );

                const lockedButton = <span title={"Locked"}>🔒</span>;

                if (isLocked && !adminMode) {
                  actionCell = lockedButton;
                } else {
                  actionCell = isBench ? startButton : benchButton;
                }
              }
            }

            if (!player && spot.startingPosition) {
              pos = spot.startingPosition;
              playerName = "empty";
              playerImage = <span title={"Empty spot"}>⚠️</span>;
            }

            return (
              <tr key={i}>
                {canChangeLineup ? <td>{actionCell}</td> : null}
                <td>{pos}</td>
                <td>{playerImage}</td>
                <td className={"left"}>{playerName}</td>
                <td className={"left"} style={{ whiteSpace: "nowrap" }}>
                  {matchupCell}
                  <br />
                  {matchupTime}
                </td>
                <td className={"right"} style={{ whiteSpace: "nowrap" }}>
                  {playerScoreCell}
                </td>
              </tr>
            );
          });

          return (
            <React.Fragment key={i}>
              {playerRows}
              {i === 0 ? headerRow : null}
            </React.Fragment>
          );
        })}
      </tbody>
    </StyledTable>
  );
}
